import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, LogOut, Users, Settings, User, Wrench } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin, canAccessTool } = useAuth();
  const [tools, setTools] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchTools();
    if (isAdmin) {
      fetchPendingCount();
    }
  }, [user]);

  const fetchTools = async () => {
    try {
      const response = await axios.get(`${API}/tools`);
      setTools(response.data);
    } catch (error) {
      console.error('Error fetching tools:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const response = await axios.get(`${API}/users/stats`);
      setPendingCount(response.data.pending);
    } catch (error) {
      console.error('Error fetching pending count:', error);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleOpenTool = (tool) => {
    if (tool.type === 'external') {
      window.open(tool.url, '_blank', 'noopener,noreferrer');
    } else if (tool.type === 'internal') {
      navigate(tool.route);
    }
  };

  // Filter tools based on user access
  const accessibleTools = tools.filter(tool => {
    if (tool.status !== 'active') return false;
    return canAccessTool(tool.slug);
  });

  const hasNoToolAccess = !isAdmin && accessibleTools.length === 0;

  return (
    <div className="bg-black text-white pt-24 min-h-screen">
      {/* Header */}
      <section className="py-12 bg-gradient-to-br from-black to-gray-900 border-b border-gray-900">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                Welcome, <span className="text-[#F4B400]">{user.name}</span>
              </h1>
              <p className="text-gray-400">Access your exclusive tools and resources</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center gap-4">
              {isAdmin && (
                <Badge className="bg-[#F4B400] text-black hover:bg-[#d49f00] px-4 py-2">
                  Admin
                </Badge>
              )}
              <Button
                onClick={() => navigate('/profile')}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:border-[#F4B400] hover:text-[#F4B400]"
              >
                <User className="mr-2" size={18} />
                Profile
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black"
              >
                <LogOut className="mr-2" size={18} />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Admin Quick Actions - Only User Alerts */}
      {isAdmin && (
        <section className="py-8 bg-gradient-to-r from-purple-900/20 to-black border-b border-gray-900">
          <div className="container mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Settings className="text-purple-400" size={24} />
                <span className="text-lg font-semibold">Admin Panel</span>
              </div>
              <div className="flex gap-3">
                {isSuperAdmin && (
                  <Button
                    onClick={() => navigate('/dashboard/admin/tools')}
                    variant="outline"
                    className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black"
                    data-testid="manage-tools-btn"
                  >
                    <Wrench className="mr-2" size={18} />
                    Manage Tools
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/dashboard/admin/users')}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Users className="mr-2" size={18} />
                  Manage Users
                  {pendingCount > 0 && (
                    <Badge className="ml-2 bg-yellow-500 text-black">
                      {pendingCount} pending
                    </Badge>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Tools Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-2">Enrigo Tools</h2>
            <p className="text-gray-400">Professional tools designed to enhance your workflow</p>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4B400] mx-auto mb-4"></div>
              <p className="text-gray-400">Loading tools...</p>
            </div>
          ) : hasNoToolAccess ? (
            <Card className="bg-white/5 border-white/10 max-w-lg mx-auto">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="text-gray-500" size={32} />
                </div>
                <h3 className="text-xl font-semibold mb-2">No Tools Assigned</h3>
                <p className="text-gray-400 mb-4">
                  You don't have access to any tools yet. Please contact the administrator to request access.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {accessibleTools.map((tool) => {
                const IconComponent = require('lucide-react')[tool.icon];
                return (
                  <Card 
                    key={tool._id}
                    className="group bg-white text-black hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#F4B400]"
                    data-testid={`tool-card-${tool.slug}`}
                  >
                    <CardContent className="p-8">
                      <div className="bg-[#F4B400] w-16 h-16 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="text-black" size={32} />
                      </div>
                      <h3 className="text-2xl font-bold mb-3">{tool.name}</h3>
                      <p className="text-gray-600 mb-6 leading-relaxed">{tool.description}</p>
                      <Badge className="bg-black text-[#F4B400] mb-6">{tool.category}</Badge>
                      <Button
                        onClick={() => handleOpenTool(tool)}
                        className="w-full bg-black text-[#F4B400] border border-[#F4B400] hover:bg-[#F4B400] hover:text-black transition-all duration-300"
                        data-testid={`tool-btn-${tool.slug}`}
                      >
                        {tool.type === 'external' ? 'Open Tool' : 'Open Dashboard'}
                        <ExternalLink className="ml-2" size={18} />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Placeholder for future tools - only show if user has some access */}
              {accessibleTools.length > 0 && (
                <Card className="border-2 border-dashed border-gray-300 bg-transparent text-white">
                  <CardContent className="p-8 h-full flex flex-col items-center justify-center text-center">
                    <div className="text-gray-500 mb-4">
                      <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-400 mb-2">More Tools Coming Soon</h3>
                    <p className="text-gray-500 text-sm">Stay tuned for additional professional tools</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-8 text-center">Quick Access</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Button
              onClick={() => { navigate('/portfolio'); window.scrollTo(0, 0); }}
              variant="outline"
              className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black h-16 text-lg"
            >
              View Portfolio
            </Button>
            <Button
              onClick={() => { navigate('/shop'); window.scrollTo(0, 0); }}
              variant="outline"
              className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black h-16 text-lg"
            >
              Shop Collection
            </Button>
            <Button
              onClick={() => { navigate('/blog/submit'); window.scrollTo(0, 0); }}
              variant="outline"
              className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black h-16 text-lg"
            >
              Submit Article
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
